import React, { useState, useEffect } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const Login = ({ setUser }) => {
  const [loginStep, setLoginStep] = useState('options');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [otpResendTime, setOtpResendTime] = useState(0);
  const navigate = useNavigate();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user) {
      navigate('/user/dashboard');
    }
  }, [navigate]);

  useEffect(() => {
    if (otpResendTime > 0) {
      const timer = setTimeout(() => setOtpResendTime(otpResendTime - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpResendTime]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
  
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }
  
    try {
      const response = await axios.post(`${API_BASE_URL}/send-otp`, { email });
      if (response.data.success) {
        setLoginStep('otp');
        setOtpResendTime(60);
      } else {
        setError(response.data.error || 'Failed to send OTP');
      }
    } catch (err) {
      let errorMsg = 'Could not connect to server. Please try again.';
      
      if (err.response) {
        if (err.response.status === 429) {
          errorMsg = 'Too many attempts. Please wait before requesting another OTP.';
        } else if (err.response.data && err.response.data.error) {
          errorMsg = err.response.data.error;
        }
      } else if (err.request) {
        errorMsg = 'No response from server. Please check your connection.';
      }
      
      setError(errorMsg);
      console.error('OTP Send Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (otpResendTime > 0) return;
    
    setError('');
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API_BASE_URL}/send-otp`, { email });
      if (response.data.success) {
        setOtpResendTime(60);
        setError('New OTP sent successfully');
      } else {
        setError(response.data.error || 'Failed to resend OTP');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Could not resend OTP. Please try again.';
      if (err.response?.status === 429) {
        setError('Too many attempts. Please wait before requesting another OTP.');
      } else {
        setError(errorMsg);
      }
      console.error('OTP Resend Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // ============ FIXED OTP VERIFICATION ============
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // CRITICAL FIX: Keep OTP as string to preserve leading zeros
    const cleanedOtp = otp.replace(/\D/g, '');

    if (cleanedOtp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      setIsLoading(false);
      return;
    }

    try {
      // Send OTP as string (not number)
      const response = await axios.post(`${API_BASE_URL}/verify-otp`, { 
        email: email.trim(), 
        otp: cleanedOtp  // Keep as string
      });

      if (response.data.success) {
        const { user } = response.data;
        // Verify user has _id
        if (!user._id) {
          throw new Error('User ID not found in response');
        }
        localStorage.setItem('user', JSON.stringify(user));
        if (setUser) setUser(user);
        navigate('/user/dashboard');
      } else {
        setError(response.data.error || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      let errorMsg = 'Verification failed. Please try again.';
      
      if (err.response) {
        if (err.response.status === 400) {
          errorMsg = err.response.data.error || 'Invalid OTP or expired.';
        } else if (err.response.status === 429) {
          errorMsg = 'Too many attempts. Please wait before trying again.';
        }
      }
      
      setError(errorMsg);
      console.error('OTP Verification Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // ============ FIXED GOOGLE LOGIN ============
  const handleGoogleSuccess = async (tokenResponse) => {
    setIsLoading(true);
    setError('');

    try {
      // Get user info from Google
      const userInfo = await axios.get(
        'https://www.googleapis.com/oauth2/v3/userinfo',
        { headers: { Authorization: `Bearer ${tokenResponse.access_token}` } }
      );

      // Send to backend
      const response = await axios.post(`${API_BASE_URL}/google-login`, {
        email: userInfo.data.email,
        name: userInfo.data.name
      });

      if (response.data.success) {
        const user = response.data.user;
        // Verify user has _id
        if (!user._id) {
          throw new Error('User ID not found in response');
        }
        localStorage.setItem('user', JSON.stringify(user));
        if (setUser) setUser(user);
        navigate('/user/dashboard');
      } else {
        setError(response.data.error || 'Google login failed');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Google login failed. Please try another method.');
      console.error('Google Login Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: () => setError('Google login failed. Please try again.')
  });

  const renderLoginOptions = () => (
    <div className="login-options container mt-5" style={{ maxWidth: '500px' }}>
      <div className="card shadow">
        <div className="card-body">
          <h3 className="card-title text-center mb-4">Login to your account</h3>
          
          {error && <div className="alert alert-danger">{error}</div>}

          <form onSubmit={handleSendOtp}>
            <div className="form-group mb-3">
              <input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="form-control form-control-lg"
              />
            </div>
            <button 
              type="submit" 
              className="btn btn-primary btn-lg w-100 mb-3"
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send OTP'}
            </button>
          </form>

          <div className="divider text-center my-4">
            <span className="px-2 bg-white">OR</span>
          </div>

          <div className="social-login">
            <button 
              onClick={() => googleLogin()}
              className="btn btn-danger btn-lg w-100"
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Login with Google'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ============ FIXED OTP INPUT ============
  const renderOtpForm = () => (
    <div className="otp-form container mt-5" style={{ maxWidth: '500px' }}>
      <div className="card shadow">
        <div className="card-body">
          <h3 className="card-title text-center mb-4">Enter OTP sent to {email}</h3>
          
          {error && <div className="alert alert-danger">{error}</div>}

          <form onSubmit={handleVerifyOtp}>
            <div className="form-group mb-3">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="6-digit OTP"
                value={otp}
                onChange={(e) => {
                  // Only allow numbers and limit to 6 digits
                  const cleanedValue = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setOtp(cleanedValue);
                }}
                required
                className="form-control form-control-lg text-center"
                autoFocus
                maxLength="6"
              />
            </div>
            <button 
              type="submit" 
              className="btn btn-primary btn-lg w-100 mb-3"
              disabled={isLoading}
            >
              {isLoading ? 'Verifying...' : 'Verify OTP'}
            </button>
          </form>

          <div className="text-center">
            <button 
              onClick={handleResendOtp}
              className="btn btn-link"
              disabled={otpResendTime > 0 || isLoading}
            >
              {otpResendTime > 0 ? `Resend OTP in ${otpResendTime}s` : 'Resend OTP'}
            </button>
          </div>

          <button 
            onClick={() => {
              setLoginStep('options');
              setOtp('');
            }}
            className="btn btn-link w-100"
          >
            Back to login options
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="user-login-page">
      {loginStep === 'options' && renderLoginOptions()}
      {loginStep === 'otp' && renderOtpForm()}
    </div>
  );
};

export default Login;